import type { ReactElement } from 'react';
import { Box, Group, Stack, Text } from '@mantine/core';
import type { VizAccentTokens } from '@/design/VizAccents';
import { formatSignalValue } from './SignalFormatting';
import type { SignalData } from './SignalTypes';

const MAX_BARS = 5;

function segmentOpacity(rank: number): number {
    return Math.max(0.3, 1 - rank * 0.16);
}

/** Horizontal ranked bars — a label, its formatted value, and a proportional bar. Shared by the signal tiles and the cost drill breakdown. */
export default function SignalBars({ data, tokens }: { data: SignalData; tokens: Pick<VizAccentTokens, 'solid'> }): ReactElement {
    const series = (data.series ?? []).slice(0, MAX_BARS);
    const max = series.reduce((peak, d) => Math.max(peak, d.value), 0) || 1;
    return (
        <Stack gap={9}>
            {series.map((d, index) => (
                <Box key={`${index}-${d.label}`}>
                    <Group justify="space-between" gap="sm" mb={3} wrap="nowrap">
                        <Text size="xs" fw={600} truncate style={{ minWidth: 0 }}>
                            {d.label}
                        </Text>
                        <Text size="xs" ff="monospace" c="dimmed" style={{ flexShrink: 0 }}>
                            {formatSignalValue(data.format, d.value)}
                        </Text>
                    </Group>
                    <Box style={{ height: 6, background: 'var(--bm-color-surface)' }}>
                        <Box style={{ height: '100%', width: `${(d.value / max) * 100}%`, background: tokens.solid, opacity: segmentOpacity(index) }} />
                    </Box>
                </Box>
            ))}
        </Stack>
    );
}
